import React, { useEffect, useState } from "react";
import { Container, EmptyContainer, NoTicketsMessage, PaginationContainer } from "src/components/ListScreen";
import { Content, ContentFrame, colors } from "src/styles";
import { Spinner } from "react-bootstrap";
import { Pagination } from "src/components/Pagination";
import { useNavigate } from "react-router-dom";
import { AssetsTable } from "./AssetsTable";
import { useIsMobile } from "src/hooks/useIsMobile";
import { Asset } from "src/types";
import { FirebaseDataService } from "src/services/Firebase/data";
import { useDispatch, useSelector } from "react-redux";
import { NumberParam, StringParam, useQueryParam } from "use-query-params";
import { setPageNameHeader } from "src/redux/actions/header";
import { setOpenedAssetsPage as setPageStore } from "src/redux/actions/openedPages";
import { getOpenedAssetsPage } from "src/redux/selectors/openedPages";
import { DEFAULT_PAGE_SIZE, INITIAL_LAST_PAGE } from "./constants";
import { AssetsList } from "./AssetsList";
import AssetsSearch from "./components/AssetsSearch";
import { CreateButtonWrapper, SearchHeaderContainer } from "./styles";
import Button from "src/components/Button";
import { faDownload } from "@fortawesome/pro-light-svg-icons";
import { SearchInputWrapper } from "src/components/atoms/SearchInput/styles";
import { getUserData } from "src/redux/selectors/user";
import styled from "styled-components";

export const AssetsTab = () => {
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const openedAssetsPageStore = useSelector(getOpenedAssetsPage);

  const [assets, setAssets] = useState<Asset[]>([]);
  const [currentAssetsPage, setCurrentAssetsPage] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportingAssets, setIsExportingAssets] = useState(false);
  const [searchInput, setSearchInput] = useState<string>("");

  const { id: employeeId, isManager, clientId } = useSelector(getUserData);

  const [openedAssetsPage, setOpenedAssetsPage] = useQueryParam("openedAssetsPage", NumberParam);
  const [appliedSearch, setAppliedSearch] = useQueryParam("assetsAppliedSearch", StringParam);
  const [searchText, setSearchText] = useQueryParam("assetsSearchText", StringParam);
  const [lastPage, setLastPage] = useState<number>(INITIAL_LAST_PAGE);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [totalAssets, setTotalAssets] = useState<number>(0);

  const pageNum = openedAssetsPage || openedAssetsPageStore || 1;

  useEffect(() => {
    if (!openedAssetsPage && openedAssetsPageStore && openedAssetsPageStore !== 1) {
      setOpenedAssetsPage(openedAssetsPageStore);
    }
  }, [openedAssetsPage, openedAssetsPageStore, setOpenedAssetsPage]);

  useEffect(() => {
    dispatch(setPageNameHeader("Assets"));
    return () => {
      dispatch(setPageNameHeader(""));
    };
  }, [dispatch]);

  const getAssets = async (searchKeyword?: string) => {
    setIsLoading(true);
    try {
      const keyword = searchKeyword?.trim();

      if (keyword) {
        const { data: firstPageData } = await FirebaseDataService.getAssets(
          undefined,
          DEFAULT_PAGE_SIZE,
          1,
          isManager ? employeeId : clientId,
          isManager,
        );
        const firstPageAssets = firstPageData.assets || [];

        const allAssets =
          firstPageData.total > firstPageAssets.length
            ? (
                await FirebaseDataService.getAssets(
                  undefined,
                  firstPageData.total,
                  1,
                  isManager ? employeeId : clientId,
                  isManager,
                )
              ).data.assets || firstPageAssets
            : firstPageAssets;

        const normalizedKeyword = keyword.toLowerCase();
        const filteredAssets = allAssets.filter((asset) => {
          const searchableText = [
            asset.assetnum,
            asset.assetCode,
            asset.description,
            asset.location,
            asset.location_description,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return searchableText.includes(normalizedKeyword);
        });

        const pagedAssets = filteredAssets.slice((pageNum - 1) * pageSize, pageNum * pageSize);

        setAssets(filteredAssets);
        setCurrentAssetsPage(pagedAssets);
        setTotalAssets(filteredAssets.length);
        setLastPage(Math.ceil(filteredAssets.length / pageSize));
      } else {
        const { data } = await FirebaseDataService.getAssets(
          undefined,
          pageSize,
          pageNum,
          isManager ? employeeId : clientId,
          isManager,
        );
        console.log("Fetched assets data:", data);
        setAssets(data.assets);
        setCurrentAssetsPage(data.assets);
        setTotalAssets(data.total);
        setLastPage(Math.ceil(data.total / pageSize));
      }
    } catch (error) {
      console.error("Error fetching assets:", error);
      setAssets([]);
      setCurrentAssetsPage([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAssets(searchText ?? undefined);
  }, [pageNum, pageSize, searchText]);

  const onPageChangeHandler = (page: number) => {
    setOpenedAssetsPage(page);
    dispatch(setPageStore(page));
  };

  const onAssetClickHandler = (asset: Asset) => {
    navigate(`/assets/${asset.assetnum}`, { state: { asset } });
  };

  const onClickApplySearch = async () => {
    setAppliedSearch("true");
    setOpenedAssetsPage(1);
    dispatch(setPageStore(1));
    setSearchText(searchInput);
    await getAssets(searchInput);
  };

  const onClickCancelSearch = () => {
    setAppliedSearch(null);
    setSearchInput("");
    setSearchText(null);
  };

  const onClickClearSearch = async () => {
    setAppliedSearch(null);
    setSearchInput("");
    setSearchText(null);
    setOpenedAssetsPage(1);
    dispatch(setPageStore(1));
    await getAssets("");
  };

  const formatInstallYear = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.getFullYear().toString();
    } catch {
      return dateString;
    }
  };

  const fetchAllAssets = async (): Promise<Asset[]> => {
    const { data: firstPageData } = await FirebaseDataService.getAssets(
      undefined,
      DEFAULT_PAGE_SIZE,
      1,
      isManager ? employeeId : clientId,
      isManager,
    );
    const firstPageAssets = firstPageData.assets || [];

    if (firstPageData.total <= firstPageAssets.length) {
      return firstPageAssets;
    }

    const { data: allAssetsData } = await FirebaseDataService.getAssets(
      undefined,
      firstPageData.total,
      1,
      isManager ? employeeId : clientId,
      isManager,
    );
    return allAssetsData.assets || firstPageAssets;
  };

  const createCsvContent = (rows: string[][]) => {
    const escapeCell = (cell: string) => `"${String(cell).replace(/"/g, '""')}"`;
    return rows.map((row) => row.map(escapeCell).join(",")).join("\n");
  };

  const downloadCsv = (csvContent: string, filename: string) => {
    const csvWithBom = `\uFEFF${csvContent}`;
    const blob = new Blob([csvWithBom], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const onClickExportAssets = async () => {
    setIsExportingAssets(true);

    try {
      const allAssets = await fetchAllAssets();

      if (!allAssets.length) {
        return;
      }

      const headers = [
        "Site Name",
        "Asset Code",
        "Description",
        "Category",
        "Location of Asset",
        "Install Year",
        "Status",
      ];
      const rows = allAssets.map((asset) => [
        asset.location_description || "N/A",
        asset.assetnum || asset.assetCode || "N/A",
        asset.description || "N/A",
        asset.assettype_description || asset.assettype || asset.category || "N/A",
        asset.location || "N/A",
        formatInstallYear(asset.installdate) || asset.installYear || "N/A",
        asset.status_description || asset.status || "N/A",
      ]);

      const csvContent = createCsvContent([headers, ...rows]);
      const exportDate = new Date().toISOString().split("T")[0];
      downloadCsv(csvContent, `assets-${exportDate}.csv`);
    } catch (error) {
      console.error("Error exporting assets:", error);
    } finally {
      setIsExportingAssets(false);
    }
  };

  return (
    <Content>
      <ContentFrame>
        {/* <TopBar>
          <BreadCrumbs folderStack={ASSETS_BREADCRUMBS} onClickItem={onBreadcrumbClickHandler} />
        </TopBar> */}

        <SearchHeaderContainer>
          <SearchInputWrapper>
            <AssetsSearch
              value={searchInput}
              applied={!!appliedSearch}
              onChange={setSearchInput}
              onClickCancel={onClickCancelSearch}
              onClickApply={onClickApplySearch}
              onClickClear={onClickClearSearch}
            />
          </SearchInputWrapper>
          <CreateButtonWrapper>
            <Button
              text="Export Assets"
              variant="white"
              loading={isExportingAssets}
              disabled={isLoading || isExportingAssets}
              additionalStyles={{ display: "flex" }}
              icon={faDownload}
              onClick={onClickExportAssets}
            />
          </CreateButtonWrapper>
        </SearchHeaderContainer>

        {isLoading ? (
          <Container>
            <Spinner animation="border" />
          </Container>
        ) : (
          <>
            {currentAssetsPage.length > 0 ? (
              <>
                {isMobile ? (
                  <AssetsList data={currentAssetsPage} onAssetClick={onAssetClickHandler} />
                ) : (
                  <Panel>
                    <PanelHeader>
                      <PanelTitle>All Assets</PanelTitle>
                      <PanelMeta>{`${currentAssetsPage.length} shown`}</PanelMeta>
                    </PanelHeader>
                    <AssetsTable data={currentAssetsPage} onRowClick={onAssetClickHandler} />
                  </Panel>
                )}
                <PaginationContainer>
                  <Pagination
                    currentPage={pageNum}
                    totalCount={totalAssets}
                    pageSize={pageSize}
                    onPageChange={onPageChangeHandler}
                  />
                </PaginationContainer>
              </>
            ) : (
              <EmptyContainer>
                <NoTicketsMessage>
                  {searchText ? "No assets found matching your search" : "No assets found"}
                </NoTicketsMessage>
              </EmptyContainer>
            )}
          </>
        )}
      </ContentFrame>
    </Content>
  );
};

const Panel = styled.section`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.white.default};
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${colors.grey.light3};
  padding: 12px 16px;
`;

const PanelTitle = styled.h2`
  margin: 0;
  color: ${colors.black.dark1};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const PanelMeta = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 12px;
`;
